import { useState, useEffect, useRef } from 'react';
import { X, Calendar, FileText, Check, ArrowLeft, Code, Sparkles, ExternalLink, Eye, Download } from 'lucide-react';
import DOMPurify from 'dompurify';
import { ArticleBlockEditor } from './ArticleBlockEditor';
import { projectService } from '../../services/api';
import { formatHtml } from '../../utils/formatHtml';
import { ArticleMetaEditor } from './ArticleMetaEditor';
import { WordPressOptimizer } from './WordPressOptimizer';
import { Button } from '../ui/Button';
import { ImageDownloadPanel } from './ImageDownloadPanel';


// Procesa HTML para que todas las imágenes usen el proxy
const proxyImagesInHtml = (html: string): string => {
    if (!html) return '';
    // Reemplaza src="http..." con src="/api/proxy-image?url=..."
    return html.replace(
        /<img([^>]*)\ssrc=["'](https?:\/\/[^"']+)["']([^>]*)>/gi,
        (match, before, url, after) => {
            // No modificar si ya está proxeado
            if (url.includes('/api/proxy-image')) return match;
            const proxiedUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
            return `<img${before} src="${proxiedUrl}"${after}>`;
        }
    );
};

interface ArticleViewerProps {
  article: any;
  onClose: () => void;
  onBack?: () => void;
  onArticleUpdate?: (updatedArticle: any) => void;
  projectId?: string;
  wordpressConnected?: boolean | null; // Para mostrar pestaña de imágenes si es false
}

// Helpers para schema (duplicados de ArticleGenerator para evitar dependencias circulares/complejas)


const stripSchemaScript = (html: string): string => {
  return html.replace(/<script type="application\/ld\+json">[\s\S]*?<\/script>/g, '');
};

export function ArticleViewer({
  article,
  onClose,
  onBack,
  onArticleUpdate,
  projectId,
  wordpressConnected
}: ArticleViewerProps) {
  // Check published status
  const isPublished =
    article.publicado ||
    article.subid_wordpress === 'si' ||
    article.subid_wordpress === 'yes' ||
    (article.fecha_programado && new Date(article.fecha_programado) <= new Date());

  const [isEditing] = useState(!isPublished);
  const [activeTab, setActiveTab] = useState<'text' | 'html' | 'meta' | 'images'>('text');

  // Mostrar pestaña de imágenes si WordPress NO está conectado (false, undefined, null, 0)
  // wordpressConnected puede ser 1 (número) desde MySQL, por eso usamos !wordpressConnected
  const showImagesTab = !wordpressConnected;

  // WordPress Optimization State
  const [showWordPressOptimizer, setShowWordPressOptimizer] = useState(false);
  const [, setWordPressStatus] = useState<'idle' | 'success' | 'error'>('idle');



  // Auto-save & Notification State
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');


  // Custom Toast Notification State
  const [toastMessage, setToastMessage] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage({ message, type });
  };

  // Prioritize article.html_content if available, then fallback to article_data structure
  // NO usar stripSchemaScript aquí - mantener scripts JSON-LD para que aparezcan en pestaña HTML
  const [editedContent, setEditedContent] = useState(
    article.html_content || article.article_data?.html || article.article_data?.content || ''
  ); // Auto-save logic

  // Reusable save function
  const saveArticle = async (contentToSave: string, showNotification = false) => {
    if (!contentToSave || !article.id) return;

    setAutoSaveStatus('saving');
    try {
      await projectService.updateArticle(article.id, {
        title: article.title,
        content: contentToSave,
        html: contentToSave,
        article_data: {
          ...article.article_data,
          content: contentToSave,
          html: contentToSave
        }
      });
      setAutoSaveStatus('saved');
      if (showNotification) showToast('Guardado correctamente', 'success');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);

      // Notify parent if callback exists
      if (onArticleUpdate) {
        onArticleUpdate({
          ...article,
          html_content: contentToSave,
          article_data: { ...article.article_data, html: contentToSave, content: contentToSave }
        });
      }
    } catch (error) {
      setAutoSaveStatus('idle');
      if (showNotification) showToast('Error al guardar', 'error');
    }
  };

  useEffect(() => {
    if (isPublished || !article.id) return;

    const timeoutId = setTimeout(() => {
      saveArticle(editedContent);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [editedContent, article.id, isPublished]);

  // Wrapper for Close/Back to ensure save
  const handleClose = async () => {
    // If saving in progress or pending changes, force save? 
    // Simplified: Just save current state before closing. 
    // But since we can't await easily in all contexts, we just trigger it.
    // For navigation, better to await if possible.
    await saveArticle(editedContent);
    onClose();
  };

  const handleBack = async () => {
    if (onBack) {
      await saveArticle(editedContent);
      onBack();
    }
  };

  // Update editedContent when article prop changes (if switching articles)
  useEffect(() => {
    let cleanHtml = article.html_content || article.article_data?.html || article.article_data?.content || '';

    // Remove the specific FAQ script if it's visible as text
    const scriptText = "const faqQuestions=document.querySelectorAll('.faq-question');faqQuestions.forEach(q=>{q.addEventListener('click',()=>{q.classList.toggle('active');const a=q.nextElementSibling;a.classList.toggle('active');});});";
    // Also try to remove it if it's inside a script tag but rendered as text
    cleanHtml = cleanHtml.replace(scriptText, '');
    // Clean up potential left-over empty script tags
    cleanHtml = cleanHtml.replace(/<script>\s*<\/script>/g, '');

    // NO usar stripSchemaScript - mantener scripts JSON-LD para pestaña HTML
    setEditedContent(cleanHtml);
  }, [article.id]); // Only reset when ID changes
  const panelRef = useRef<HTMLDivElement>(null);

  const articleData = article.article_data || {};
  const { wordCount, readingTime } = articleData;
  // Fallback to top-level title if not in data blob
  const title = articleData.title || article.title || '';
  // Usar estado local para el contenido mostrado, inicializado con el artículo
  // Usar estado local para el contenido mostrado, inicializado con el artículo
  // Separamos el bloque META del contenido editable para evitar que Quill lo borre
  const extractMetaBlock = (html: string) => {
    const match = html.match(/<div id="meta">[\s\S]*?<\/div>/);
    return match ? match[0] : '';
  };

  const stripMetaBlock = (html: string) => {
    return html.replace(/<div id="meta">[\s\S]*?<\/div>/, '');
  };

  // Normaliza el HTML quitando indentaciones y saltos de línea extras
  // para que la Vista Previa no se rompa al editar en pestaña HTML
  const normalizeHtml = (html: string): string => {
    if (!html?.trim()) return '';
    // Usar DOMParser para normalizar el HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Obtener el innerHTML normalizado (sin indentaciones extras)
    return doc.body.innerHTML;
  };

  // Initial state setup
  const initialFullHtml = article.html_content || article.article_data?.html || article.article_data?.content || '';
  const [metaBlock, setMetaBlock] = useState(extractMetaBlock(initialFullHtml));
  const [currentContent, setCurrentContent] = useState(initialFullHtml);

  // FAQ Accordion Script Logic for Preview
  // FAQ Accordion Script Logic for View Mode (Not Editing)
  useEffect(() => {
    // Only run if we are NOT in editing mode (i.e. viewing the rendered HTML)
    // or if the article is published (which forces view mode)
    if (!isEditing || isPublished) {
      // Small timeout to ensure DOM is ready after React render
      const timer = setTimeout(() => {
        const faqQuestions = document.querySelectorAll('.faq-question');

        const handleClick = (e: Event) => {
          const q = e.currentTarget as HTMLElement;
          q.classList.toggle('active');
          const a = q.nextElementSibling;
          if (a) {
            a.classList.toggle('active');
          }
        };

        faqQuestions.forEach(q => {
          // Remove old listeners to prevent duplicates
          q.removeEventListener('click', handleClick);
          q.addEventListener('click', handleClick);
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentContent, isEditing, isPublished]);

  const [projectDetails] = useState<any>(null);

  const createdAt = new Date(article.created_at).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Bloquear scroll del body cuando el modal está abierto
  /* REMOVED per user request to avoid "unwanted SELECT"
  useEffect(() => {
    // Determine effective Project ID
    const effectiveProjectId = projectId || article.project_id || article.projectId;

    if (effectiveProjectId) {
      projectService.getProject(effectiveProjectId).then(details => {
        if (details) setProjectDetails(details);
      }).catch(err => { });
    }
  }, [projectId, article]);
  */

  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);







  return (
    <div
      aria-modal="true"
      role="dialog"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full max-w-5xl max-h-[calc(100vh-2rem)] md:max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl outline-none"
      >
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate mb-1 sm:mb-2">
                {title || 'Sin título'}
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span className="hidden sm:inline">{createdAt}</span>
                  <span className="sm:hidden">{new Date(article.created_at).toLocaleDateString('es-ES')}</span>
                </div>
                <div className="flex items-center">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  {wordCount || 0} palabras
                </div>
                {readingTime && (
                  <div className="hidden sm:block">
                    {readingTime} min. lectura
                  </div>
                )}



                {(article.link || articleData.link) && (
                  <a
                    href={article.link || articleData.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary-600 hover:text-primary-700 hover:underline transition-colors ml-2"
                    title="Ver en WordPress"
                  >
                    <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">WP</span>
                  </a>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Auto-save Indicator */}
              {!isPublished && (
                <div className="text-xs font-medium mr-2 text-gray-500 transition-colors">
                  {autoSaveStatus === 'saving' && <span className="text-blue-600 animate-pulse">Guardando...</span>}
                  {autoSaveStatus === 'saved' && <span className="text-green-600">Guardado</span>}
                </div>
              )}

              {/* Only show Optimize button if NOT published */}
              {!isPublished && (
                <Button
                  onClick={() => setShowWordPressOptimizer(true)}
                  variant="primary"
                  size="sm"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 hidden md:flex"
                  icon={<Sparkles className="w-4 h-4" />}
                >
                  Optimizar WP
                </Button>
              )}

              {onBack && (
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-1.5 rounded-md border-2 border-primary-600 bg-white px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Volver</span>
                </button>
              )}

              {/* Only show Save button if NOT published */}
              {!isPublished && (
                <button
                  onClick={() => saveArticle(editedContent, true)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Guardar</span>
                </button>
              )}

              <button
                onClick={handleClose}
                className="p-1.5 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Toast Notification */}
          {toastMessage && (
            <div className={`absolute top-20 right-4 z-[60] px-4 py-3 rounded shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${toastMessage.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
              {toastMessage.type === 'success' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              <span className="text-sm font-medium">{toastMessage.message}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8">
          {/* If published, force view mode (render HTML directly). If not published, show tabs/editor */}
          {!isPublished && isEditing ? (
            <div className="flex flex-col h-full">
              {/* Tabs de edición */}
              <div className="flex items-center justify-between border-b border-gray-200 mb-4">
                <div className="flex overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('text')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'text'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <Eye className="w-4 h-4" />
                    Vista Previa
                  </button>
                  <button
                    onClick={() => setActiveTab('html')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'html'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <Code className="w-4 h-4" />
                    HTML
                  </button>
                  <button
                    onClick={() => setActiveTab('meta')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'meta'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <FileText className="w-4 h-4" />
                    Meta
                  </button>
                  {showImagesTab && (
                    <button
                      onClick={() => setActiveTab('images')}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'images'
                        ? 'border-green-600 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      <Download className="w-4 h-4" />
                      Imagenes
                    </button>
                  )}
                </div>

                {/* Botón Previsualizar (Movido aquí) */}
                <button
                  onClick={() => {
                    const fullContent = formatHtml(metaBlock + '\n' + editedContent)
                      // Clean internal links for preview
                      .replace(/<a\s+([^>]*?)href="#([^"]*)"([^>]*?)target="_blank"([^>]*?)>/gi, '<a $1href="#$2"$3$4>')
                      .replace(/<a\s+([^>]*?)target="_blank"([^>]*?)href="#([^"]*)"([^>]*?)>/gi, '<a $1$2href="#$3"$4>');
                    const previewWindow = window.open('', '_blank');
                    if (previewWindow) {
                      previewWindow.document.write(`
                        <!DOCTYPE html>
                        <html lang="es">
                        <head>
                          <meta charset="UTF-8">
                          <meta name="viewport" content="width=device-width, initial-scale=1.0">
                          <title>${title || 'Vista Previa'}</title>
                          <script src="https://cdn.tailwindcss.com"></script>
                          <style>
                            body { background-color: #f3f4f6; padding: 2rem; }
                            article { background-color: white; max-width: 65ch; margin: 0 auto; padding: 2rem; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                            article img { max-width: 100%; height: auto; border-radius: 0.375rem; margin: 1.5rem 0; }
                            article h1 { font-size: 2.25rem; font-weight: 800; margin-bottom: 1.5rem; line-height: 1.1; }
                            article h2 { font-size: 1.5rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; color: #111827; }
                            article h3 { font-size: 1.25rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #374151; }
                            article p { margin-bottom: 1.25rem; line-height: 1.75; color: #374151; text-align: justify; }
                            article ul, article ol { margin-bottom: 1.25rem; padding-left: 1.5rem; }
                            article ul { list-style-type: disc; }
                            article ol { list-style-type: decimal; }
                            article li { margin-bottom: 0.5rem; color: #374151; }
                            article blockquote { border-left: 4px solid #e5e7eb; padding-left: 1rem; font-style: italic; color: #4b5563; margin: 1.5rem 0; }
                            nav#indice { background: #f9fafb; padding: 1.5rem; border-radius: 0.5rem; margin-bottom: 2rem; }
                            nav#indice h2 { margin-top: 0; font-size: 1.25rem; }
                            nav#indice a { color: #2563eb; text-decoration: none; }
                            nav#indice a:hover { text-decoration: underline; }

                            /* FAQ Styles */
                            .faq-question {
                              cursor: pointer;
                              padding: 1rem;
                              background-color: #f9fafb;
                              border-radius: 0.5rem;
                              margin-bottom: 0.5rem;
                              font-weight: 600;
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                            }
                            .faq-question:after {
                              content: '+';
                              font-size: 1.25rem;
                            }
                            .faq-question.active:after {
                              content: '-';
                            }
                            .faq-answer {
                              display: none;
                              padding: 1rem;
                              border-left: 2px solid #e5e7eb;
                              margin-bottom: 1rem;
                            }
                            .faq-answer.active {
                              display: block;
                            }

                            /* WordPress FAQ Blocks - Yoast */
                            .schema-faq-section {
                              margin-bottom: 1.5rem;
                              padding-bottom: 1rem;
                              border-bottom: 1px solid #e5e7eb;
                            }
                            .schema-faq-section:last-child {
                              border-bottom: none;
                              margin-bottom: 0;
                            }
                            .schema-faq-question {
                              display: block;
                              margin-bottom: 0.5rem;
                              font-size: 1.1rem;
                            }
                            .schema-faq-answer {
                              color: #4b5563;
                              line-height: 1.6;
                            }

                            /* WordPress FAQ Blocks - RankMath */
                            .rank-math-faq-item {
                              margin-bottom: 1.5rem;
                              padding-bottom: 1rem;
                              border-bottom: 1px solid #e5e7eb;
                            }
                            .rank-math-faq-item:last-child {
                              border-bottom: none;
                              margin-bottom: 0;
                            }
                            .rank-math-question {
                              margin-bottom: 0.5rem;
                              font-size: 1.1rem;
                            }
                            .rank-math-answer {
                              color: #4b5563;
                              line-height: 1.6;
                            }

                            /* HTML5 Details/Summary FAQ */
                            section.faqs details {
                              margin-bottom: 1rem;
                              border: 1px solid #e5e7eb;
                              border-radius: 0.5rem;
                              overflow: hidden;
                            }
                            section.faqs details summary {
                              padding: 1rem;
                              background-color: #f9fafb;
                              cursor: pointer;
                              font-weight: 600;
                            }
                            section.faqs details[open] summary {
                              border-bottom: 1px solid #e5e7eb;
                            }
                            section.faqs details p {
                              padding: 1rem;
                              margin: 0;
                            }

                            /* Simple H3/P FAQ */
                            section.faqs h3 {
                              margin-top: 1.5rem;
                              margin-bottom: 0.5rem;
                            }
                            section.faqs h3:first-of-type {
                              margin-top: 0;
                            }

                            /* DL/DT/DD FAQ List */
                            section.faqs dl.faq-list {
                              margin: 0;
                              padding: 0;
                            }
                            section.faqs dl.faq-list dt {
                              font-weight: 600;
                              font-size: 1.1rem;
                              color: #111827;
                              padding: 1rem;
                              background-color: #f9fafb;
                              border-radius: 0.5rem 0.5rem 0 0;
                              margin-top: 1rem;
                              border: 1px solid #e5e7eb;
                              border-bottom: none;
                            }
                            section.faqs dl.faq-list dt:first-of-type {
                              margin-top: 0;
                            }
                            section.faqs dl.faq-list dd {
                              margin: 0;
                              padding: 1rem;
                              color: #374151;
                              line-height: 1.6;
                              border: 1px solid #e5e7eb;
                              border-top: none;
                              border-radius: 0 0 0.5rem 0.5rem;
                              background-color: white;
                            }
                          </style>
                        </head>
                        <body>
                          <article class="prose lg:prose-xl">
                            ${fullContent}
                          </article>
                          <script>
                            document.addEventListener('DOMContentLoaded', () => {
                              const faqQuestions = document.querySelectorAll('.faq-question');
                              faqQuestions.forEach(q => {
                                q.addEventListener('click', () => {
                                  q.classList.toggle('active');
                                  const a = q.nextElementSibling;
                                  if (a) a.classList.toggle('active');
                                });
                              });
                            });
                          </script>
                        </body>
                        </html>
                      `);
                      previewWindow.document.close();
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors mr-1"
                  title="Previsualizar en nueva pestaña"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="hidden sm:inline">Previsualizar</span>
                </button>
              </div>

              {activeTab === 'text' && (
                <div className="border border-gray-200 rounded-lg bg-white">
                  <ArticleBlockEditor
                    html={editedContent}
                    onHtmlChange={(newHtml) => {
                      setEditedContent(newHtml);
                      // Also update metaBlock if the new HTML contains one
                      const newMetaBlock = extractMetaBlock(newHtml);
                      if (newMetaBlock) {
                        setMetaBlock(newMetaBlock);
                      }
                    }}
                    projectId={projectId || article.project_id}
                    sessionId={article.session_id}
                    articleTitle={title}
                  />
                </div>
              )}

              {activeTab === 'html' && (
                <textarea
                  value={formatHtml((metaBlock + '\n' + editedContent)
                    // Convertir URLs de proxy a URLs originales de WordPress
                    .replace(/src="\/api\/proxy-image\?url=([^"&]+)(?:&amp;[^"]*|&[^"]*)?" /gi, (_, encodedUrl) => {
                      try { return `src="${decodeURIComponent(encodedUrl)}" `; } catch { return _; }
                    })
                    .replace(/src="\/api\/proxy-image\?url=([^"&]+)(?:&amp;[^"]*|&[^"]*)?"(?=[^s]|$)/gi, (_, encodedUrl) => {
                      try { return `src="${decodeURIComponent(encodedUrl)}"`; } catch { return _; }
                    })
                    // Safeguard: Remove target="_blank" from internal links just in case
                    .replace(/<a\s+([^>]*?)href="#([^"]*)"([^>]*?)target="_blank"([^>]*?)>/gi, '<a $1href="#$2"$3$4>')
                    .replace(/<a\s+([^>]*?)target="_blank"([^>]*?)href="#([^"]*)"([^>]*?)>/gi, '<a $1$2href="#$3"$4>')
                  )} // HTML view shows Everything
                  onChange={(e) => {
                    // Normalizar el HTML para quitar indentaciones extras de formatHtml()
                    // Esto evita que la Vista Previa se rompa al cambiar de pestaña
                    const val = e.target.value;
                    const normalizedVal = normalizeHtml(val);
                    setMetaBlock(extractMetaBlock(normalizedVal));
                    setEditedContent(stripMetaBlock(normalizedVal));
                  }}
                  className="w-full min-h-[500px] p-4 border border-gray-700 rounded-lg bg-gray-900 text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm resize-y shadow-inner selection:bg-blue-500/30"
                  placeholder="Código HTML..."
                  spellCheck={false}
                />
              )}

              {activeTab === 'meta' && (
                <ArticleMetaEditor
                  htmlContent={editedContent}
                  initialMetadata={article.article_data?.metadata}
                  onUpdateHtml={(newHtml) => {
                    setEditedContent(newHtml);
                    // Also update current preview
                    setCurrentContent(newHtml);
                  }}
                />
              )}

              {activeTab === 'images' && showImagesTab && (
                <div className="border border-gray-200 rounded-lg bg-white h-[600px]">
                  <ImageDownloadPanel
                    projectId={projectId || article.project_id}
                    articleTitle={title}
                    brief={article.article_data?.brief}
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              className="prose prose-sm sm:prose-base lg:prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(proxyImagesInHtml(stripSchemaScript(currentContent)), { ADD_TAGS: ['iframe'], ADD_ATTR: ['target', 'rel', 'data-origin'] }) }}
            />
          )}
        </div>



        {/* Footer - Sticky */}
        <div className="sticky bottom-0 z-10 bg-white/95 backdrop-blur border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="space-y-3 sm:space-y-4">
            {/* Info del artículo */}
            {/* ID y Status removidos por petición del usuario */}
            <div className="hidden">
              <div>
                <span className="text-gray-500 font-medium">ID de Sesión:</span>
                <div className="font-mono text-gray-900 text-xs mt-0.5 break-all">{article.session_id}</div>
              </div>
              <div>
                <span className="text-gray-500 font-medium">Estado:</span>
                <div className="capitalize font-medium text-gray-900 mt-0.5">
                  {article.status === 'completed' ? 'Completado' : 'Generado'}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* WordPress Optimizer Modal */}
      {
        showWordPressOptimizer && (
          <WordPressOptimizer
            articleId={article.id}
            projectId={projectId || article.project_id || article.projectId}
            // Include metaBlock to capture featured image metadata (alt/title)
            articleContent={metaBlock + '\n' + editedContent}
            articleTitle={title}
            // SEO meta fields para plugins como Yoast, Rank Math, AIOSEO
            metaTitle={article.article_data?.metadata?.metaTitle || article.metadata?.metaTitle || title}
            metaDescription={article.article_data?.metadata?.metaDescription || article.metadata?.metaDescription || ''}
            sessionId={article.session_id} // Pass session ID
            // Tipo de artículo para determinar flujo de optimización
            articleType={article.article_data?.article_type || article.article_data?.articleType || article.articleType || 'wikipedia'}
            // Try to get images from content
            images={(metaBlock + editedContent).match(/<img[^>]+src="([^">]+)"/g)?.map((img) => img.match(/src="([^">]+)"/)?.[1] || '') || []}
            projectDetails={projectDetails}
            onClose={() => setShowWordPressOptimizer(false)}
            onSuccess={() => {
              setShowWordPressOptimizer(false);
              setWordPressStatus('success');
              showToast('Artículo enviado a WordPress correctamente', 'success');

              // Force update local status to read-only immediate
              if (onArticleUpdate) {
                onArticleUpdate({
                  ...article,
                  subid_wordpress: 'si',
                  publicado: false, // User request: "subido a wordpress si, publicado no"
                  status: 'completed'
                });
              }
            }}
          />
        )
      }
    </div >
  );
}

